// Google OAuth Service for Google Services Integration
// This handles Google OAuth flow for email, drive, and calendar access

import { createLogger } from "../utils/logger";
import { ANALYTICS_ERROR_CODES, analyticsService } from "./AnalyticsService";
import { auth } from "./firebase";

const logger = createLogger("GoogleAuthService");

interface GoogleUser {
  id: string;
  email: string;
  name: string;
  picture: string;
  accessToken: string;
  tokenExpiry?: number;
  hasCalendarPermissions?: boolean;
  hasDrivePermissions?: boolean;
  hasEmailPermissions?: boolean;
}

interface GoogleAuthConfig {
  clientId: string;
  redirectUri: string;
  scope: string;
}

class GoogleAuthService {
  private config: GoogleAuthConfig;
  private user: GoogleUser | null = null;
  private googleAuth: any = null;
  private isProcessingCallback = false;

  constructor() {
    this.config = {
      clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID || "",
      redirectUri: process.env.REACT_APP_AUTH_CALLBACK_URL || "http://localhost:3000/auth/google/callback",
      scope:
        "openid profile email https://www.googleapis.com/auth/calendar.readonly https://www.googleapis.com/auth/calendar.events " +
        "https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/drive.readonly",
    };
  }

  // Initialize Google Identity Services
  private loadGoogleScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.google) {
        resolve();
        return;
      }

      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  // Start OAuth flow with redirect using backend-generated URL
  async signIn(): Promise<void> {
    try {
      await this.loadGoogleScript();

      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("No Firebase Auth user found");
      }
      const firebaseToken = await currentUser.getIdToken();

      const response = await fetch(`${process.env.REACT_APP_FUNCTIONS_BASE_URL}/getGoogleAuthUrl`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${firebaseToken}`,
        },
        body: JSON.stringify({
          redirectUri: this.config.redirectUri,
          scope: this.config.scope,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get Google auth URL: ${errorText}`);
      }
      const data = await response.json();
      if (!data.url) {
        throw new Error("Invalid response from getGoogleAuthUrl");
      }
      window.location.href = data.url;
    } catch (error) {
      console.error("Google OAuth error:", error);
      throw error;
    }
  }

  // Request Google services permissions (Calendar, Drive, Email) via backend URL
  async requestCalendarPermissions(): Promise<void> {
    try {
      console.log("Requesting Google services permissions...");

      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("No Firebase Auth user found");
      }
      const firebaseToken = await currentUser.getIdToken();

      const response = await fetch(`${process.env.REACT_APP_FUNCTIONS_BASE_URL}/getGoogleAuthUrl`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${firebaseToken}`,
        },
        body: JSON.stringify({
          redirectUri: this.config.redirectUri,
          scope: this.config.scope,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get Google auth URL: ${errorText}`);
      }
      const data = await response.json();
      if (!data.url) {
        throw new Error("Invalid response from getGoogleAuthUrl");
      }
      window.location.href = data.url;
    } catch (error) {
      logger.error("Google services permissions request error:", error);

      // Track Google OAuth failure
      analyticsService.logError(ANALYTICS_ERROR_CODES.GOOGLE_PERMISSIONS_DENIED, error instanceof Error ? error.message : "Unknown Google permissions error", {
        component: "googleAuth.ts",
        userAction: "request_calendar_permissions",
        severity: "high",
      });

      throw error;
    }
  }

  // Handle OAuth callback and automatically connect calendar provider
  async handleCallback(): Promise<GoogleUser | null> {
    // Prevent multiple simultaneous callback processing
    if (this.isProcessingCallback) {
      logger.info("OAuth callback already being processed, returning existing user");
      return this.user;
    }

    try {
      this.isProcessingCallback = true;
      logger.info("Handling Google OAuth callback");

      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");
      const error = urlParams.get("error");
      if (error) {
        // Track OAuth error
        analyticsService.logError(ANALYTICS_ERROR_CODES.AUTH_GOOGLE_OAUTH_FAILED, `OAuth error: ${error}`, {
          component: "googleAuth.ts",
          userAction: "handle_oauth_callback",
          severity: "high",
        });
        throw new Error(`OAuth error: ${error}`);
      }

      if (!code) {
        // If no code, check if we already have a user (already processed)
        if (this.user) {
          return this.user;
        }
        throw new Error("No authorization code received");
      }

      // Exchange code for tokens
      await this.exchangeCodeForToken(code);

      // Automatically connect calendar provider after successful OAuth
      await this.connectCalendarProvider();

      // Track successful Google OAuth
      analyticsService.logAuthEvent("login", "google");
      if (this.user) {
        analyticsService.setUser(this.user as any); // Set user for analytics
      }

      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);

      return this.user;
    } catch (error) {
      logger.error("Error handling OAuth callback:", error);

      // Track OAuth callback failure
      analyticsService.logError(ANALYTICS_ERROR_CODES.AUTH_GOOGLE_OAUTH_FAILED, error instanceof Error ? error.message : "Unknown OAuth callback error", {
        component: "googleAuth.ts",
        userAction: "handle_oauth_callback",
        severity: "high",
      });

      throw error;
    } finally {
      this.isProcessingCallback = false;
    }
  }

  // Automatically connect calendar provider after Google OAuth
  private async connectCalendarProvider(): Promise<void> {
    try {
      console.log("Automatically connecting calendar provider...");

      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("No Firebase Auth user found");
      }

      // Connect to Google Calendar provider
      const response = await fetch(`${process.env.REACT_APP_FUNCTIONS_BASE_URL}/connectCalendarProvider`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await currentUser.getIdToken()}`,
        },
        body: JSON.stringify({
          providerType: "google",
          config: {
            // The backend will use the stored OAuth tokens
            connected: true,
          },
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to connect calendar provider: ${errorText}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(`Calendar provider connection failed: ${result.error || "Unknown error"}`);
      }

      console.log("Successfully connected Google Calendar provider");
    } catch (error) {
      console.error("Error connecting calendar provider:", error);
      // Don't throw here - OAuth was successful, calendar connection can be retried later
    }
  }

  // Exchange authorization code for access token
  private async exchangeCodeForToken(code: string): Promise<void> {
    try {
      console.log("Sending token exchange request...");

      // Get Firebase Auth token
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("No Firebase Auth user found");
      }
      const firebaseToken = await currentUser.getIdToken();

      const response = await fetch(`${process.env.REACT_APP_FUNCTIONS_BASE_URL}/googleAuthCallback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${firebaseToken}`,
        },
        body: JSON.stringify({
          code,
          redirectUri: this.config.redirectUri,
          clientId: this.config.clientId, // backend enforces env client
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error("Token exchange failed:", errorText);
        throw new Error("Failed to exchange code for token");
      }

      const data = await response.json();

      if (data.success && data.user) {
        this.user = data.user; // hold in memory only
        if (this.user && !this.user.tokenExpiry && data.user.accessToken) {
          this.user.tokenExpiry = Date.now() + 3600 * 1000;
        }
      } else {
        throw new Error("Invalid response format from server");
      }

      // Don't clean up URL here - let the AuthCallback component handle navigation
      console.log("User initialized from backend callback");
    } catch (error) {
      console.error("Error exchanging code for token:", error);
      throw error;
    }
  }

  // Refresh access token using stored refresh token
  private async refreshTokenIfNeeded(userId: string): Promise<void> {
    try {
      console.log("Checking if token refresh is needed...");

      const response = await fetch(`${process.env.REACT_APP_FUNCTIONS_BASE_URL}/refreshAccessToken`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userId }),
      });

      console.log("Refresh token response status:", response.status);

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.requiresReauth) {
          console.log("Refresh token invalid, requires re-authentication");
          throw new Error("REQUIRES_REAUTH");
        }
        throw new Error("Failed to refresh token");
      }

      const data = await response.json();
      console.log("Token refresh successful");

      if (data.success && data.accessToken) {
        // Update in-memory user with new access token
        if (!this.user) {
          this.user = {
            id: userId,
            email: "",
            name: "",
            picture: "",
            accessToken: data.accessToken,
            tokenExpiry: data.tokenExpiry || Date.now() + data.expiresIn * 1000,
          };
        } else {
          this.user.accessToken = data.accessToken;
          this.user.tokenExpiry = data.tokenExpiry || Date.now() + data.expiresIn * 1000;
        }
        console.log("✅ Token refreshed (memory only)");
      } else {
        throw new Error("Invalid refresh response format");
      }
    } catch (error) {
      console.error("Error refreshing token:", error);
      throw error;
    }
  }

  // Sign out
  async signOut(): Promise<void> {
    try {
      this.user = null;

      // Clean up URL and go back to root
      window.history.replaceState({}, document.title, "/");
    } catch (error) {
      console.error("Error signing out:", error);
      throw error;
    }
  }

  // Clear Google auth data and force re-authentication
  async clearAndReauthenticate(): Promise<void> {
    try {
      console.log("Clearing Google auth data and forcing re-authentication...");

      // Clear all Google-related localStorage
      this.user = null;

      // Clear any existing OAuth state
      window.history.replaceState({}, document.title, window.location.pathname);

      // Force re-authentication with consent screen
      await this.requestCalendarPermissions();
    } catch (error) {
      console.error("Error clearing and re-authenticating:", error);
      throw error;
    }
  }

  // Get current user
  getCurrentUser(): GoogleUser | null {
    return this.user;
  }

  // Get access token for API calls with automatic refresh
  async getValidAccessToken(): Promise<string | null> {
    if (!this.user) {
      return null;
    }

    // Check if token is expired or close to expiring (within 5 minutes)
    if (this.user.tokenExpiry && Date.now() > this.user.tokenExpiry - 5 * 60 * 1000) {
      console.log("Token expired or close to expiring, attempting refresh...");
      try {
        await this.refreshTokenIfNeeded(this.user.id);
        return this.user.accessToken;
      } catch (error) {
        if (error instanceof Error && error.message === "REQUIRES_REAUTH") {
          console.log("Token refresh failed, prompting for re-authentication");
          // Clear stored user and prompt for re-auth
          await this.signOut();
          await this.signIn();
          return null; // User will be redirected to OAuth
        }
        console.error("Token refresh error:", error);
        return null;
      }
    }

    return this.user.accessToken;
  }

  // Get access token for API calls (legacy method)
  getAccessToken(): string | null {
    return this.user?.accessToken || null;
  }

  // Initialize from backend status; if connected, fetch fresh access token
  async initialize(): Promise<void> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;
      const firebaseToken = await currentUser.getIdToken();

      const statusResp = await fetch(`${process.env.REACT_APP_FUNCTIONS_BASE_URL}/getGoogleAuthStatus`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${firebaseToken}`,
        },
      });
      if (!statusResp.ok) return;
      const status = await statusResp.json();
      if (status.connected && status.hasRefreshToken) {
        await this.refreshTokenIfNeeded(currentUser.uid);
      }
    } catch (error) {
      console.error("GoogleAuthService: initialize from backend failed:", error);
    }
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return this.user !== null;
  }

  // Get connection status for onboarding
  async getConnectionStatus(): Promise<{
    connected: boolean;
    hasRefreshToken: boolean;
    email?: string;
  }> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return { connected: false, hasRefreshToken: false };
      }

      const firebaseToken = await currentUser.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_FUNCTIONS_BASE_URL}/getGoogleAuthStatus`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${firebaseToken}`,
        },
      });

      if (!response.ok) {
        return { connected: false, hasRefreshToken: false };
      }

      const status = await response.json();
      return {
        connected: status.connected || false,
        hasRefreshToken: status.hasRefreshToken || false,
        email: status.email,
      };
    } catch (error) {
      console.error("Error getting connection status:", error);
      return { connected: false, hasRefreshToken: false };
    }
  }

  // Initiate authentication flow
  async initiateAuth(): Promise<void> {
    await this.signIn();
  }

  // Disconnect Google account
  async disconnect(): Promise<void> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("No authenticated user");
      }

      const firebaseToken = await currentUser.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_FUNCTIONS_BASE_URL}/disconnectGoogle`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${firebaseToken}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to disconnect Google account");
      }

      // Clear local state
      this.user = null;
    } catch (error) {
      console.error("Error disconnecting Google account:", error);
      throw error;
    }
  }

  // Check if user has calendar permissions
  async hasCalendarPermissions(): Promise<boolean> {
    if (!this.user) {
      return false;
    }

    try {
      // Try to make a simple calendar API call to check permissions
      const response = await this.makeAuthenticatedRequest("https://www.googleapis.com/calendar/v3/users/me/calendarList?maxResults=1");

      return response.ok;
    } catch (error) {
      console.log("Calendar permissions check failed:", error);
      return false;
    }
  }

  // Get user info for display
  getUserInfo() {
    if (!this.user) return null;

    return {
      name: this.user.name,
      email: this.user.email,
      picture: this.user.picture,
    };
  }

  // Make authenticated API call with automatic token refresh
  async makeAuthenticatedRequest(url: string, options: RequestInit = {}): Promise<Response> {
    const token = await this.getValidAccessToken();

    if (!token) {
      throw new Error("No valid access token available");
    }

    console.log("Making authenticated request to:", url);
    console.log("Token starts with:", token.substring(0, 20) + "...");

    const authOptions = {
      ...options,
      headers: {
        ...options.headers,
        Authorization: `Bearer ${token}`,
      },
    };

    const response = await fetch(url, authOptions);

    // If we get a 401, the token might be invalid even if it's not expired
    if (response.status === 401) {
      console.log("API call returned 401, token may be invalid");
      try {
        // Try to refresh the token
        await this.refreshTokenIfNeeded(this.user!.id);
        // Retry the request with the new token
        const newToken = await this.getValidAccessToken();
        if (newToken) {
          const retryOptions = {
            ...options,
            headers: {
              ...options.headers,
              Authorization: `Bearer ${newToken}`,
            },
          };
          return await fetch(url, retryOptions);
        }
      } catch (error) {
        if (error instanceof Error && error.message === "REQUIRES_REAUTH") {
          console.log("Token refresh failed, prompting for re-authentication");
          await this.signOut();
          await this.signIn();
          throw new Error("REQUIRES_REAUTH");
        }
      }
    }

    // If we get a 403, it might be a permissions issue
    if (response.status === 403) {
      console.log("API call returned 403, checking if permissions are needed");
      // Check which API is being called and throw appropriate error
      if (url.includes("calendar.googleapis.com")) {
        throw new Error("CALENDAR_PERMISSIONS_REQUIRED");
      } else if (url.includes("drive.googleapis.com")) {
        throw new Error("DRIVE_PERMISSIONS_REQUIRED");
      } else if (url.includes("gmail.googleapis.com")) {
        throw new Error("GMAIL_PERMISSIONS_REQUIRED");
      } else {
        throw new Error("GOOGLE_PERMISSIONS_REQUIRED");
      }
    }

    return response;
  }
}

// Create singleton instance
export const googleAuthService = new GoogleAuthService();

// Export types
export type { GoogleAuthConfig, GoogleUser };
